import { getIsStatus, toPostStatusEnum } from '@/Components/posts/status';
import type { PickFromPostCardTypes } from '@/Components/posts/ui/posts-card';
import type { Hub_Frontend_Single_Query$data, Hub_Single_Query$data } from '@/graphql/queries/Hub.query';
import { PostStatusEnum, type ZnglyVideo } from '@/graphql/types';
import isDeepEqual from 'fast-deep-equal';
import { throttle } from 'lodash';
import { useMemo } from 'react';
import type { DeepNullable, DeepWritable } from 'ts-essentials';
import { type TemporalState, temporal } from 'zundo';
import { create } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

type EditHubData = Hub_Single_Query$data['hub'];
type FrontendHubData = Hub_Frontend_Single_Query$data['hub'];

type HubType = DeepWritable<EditHubData>;
type HubConnectionEdge = NonNullable<NonNullable<EditHubData>['connectionHubToHps']>['edges'][number];
type HubMetadataShare = NonNullable<NonNullable<NonNullable<EditHubData>['metadata']>['shareIcons']>;
type PageCatalogItem = NonNullable<
  NonNullable<
    NonNullable<NonNullable<EditHubData>['connectionHubToHps']>['edges'][number]['node']['hubPage']
  >['node']['connectionHpToCs']
>['edges'][number];

type PageFormNode = NonNullable<
  NonNullable<DeepWritable<HubConnectionEdge['node']>['hubPage']>['node']['form']
>['node'];

type Actions = {
  setData: (data?: HubType | EditHubData | FrontendHubData) => void;
  addConnectionEdge: (node: DeepWritable<HubConnectionEdge['node']>) => void;
  updateConnectionEdge: (pageId: string, node: Omit<Partial<HubConnectionEdge['node']>, 'hubPage' | 'pageId'>) => void;
  deleteConnectionEdge: (pageId: string) => void;
  updateConnectionEdgePageOrder: (pageId: string, direction: 'increase' | 'decrease') => void;
  addMetadataShareIcon: (node: DeepWritable<HubMetadataShare[number]>) => void;
  updateMetadataShareIcon: (idx: number, node: Partial<HubMetadataShare[number]>) => void;
  deleteMetadataShareIcon: (idx: number) => void;
  updateConnectionCatalogItem: (pageId: string, node: DeepWritable<PageCatalogItem>, action: 'add' | 'delete') => void;
  updateConnectionPageForm: (pageId: string, node: PageFormNode | PickFromPostCardTypes<'form'>['node'] | null) => void;
  reorderConnectionCatalogItems: (
    pageId: string,
    sourceIndexOrNodes: number | ZnglyVideo[],
    destinationIndex?: number
  ) => void;
};

const initHubConfig = {
  __config: {
    isFallback: false,
    isSaving: false,
  },
};

const initHubStore = {
  id: '' as string,
  title: '' as string,
  description: '' as string,
  status: '' as PostStatusEnum,
  __id: '' as string,
  connectionHubToHps: {
    edges: [],
  },
  template: '' as string,
  group: '' as string,
  slug: '' as string,
  metadata: {
    design: null,
    navMeta: [],
    shareIcons: [],
  },
} as Partial<HubType>;

type HubStore = Partial<HubType> & typeof initHubConfig & Actions;

export const useEditHubStore = create<HubStore>()(
  temporal(
    (set) => ({
      ...initHubStore,
      ...initHubConfig,
      setData: (page) => {
        set((prev) => {
          if (!page) {
            return nullifyStoreData(prev, initHubStore);
          }

          return {
            ...(page as HubType),
            status: toPostStatusEnum(page?.status),
          };
        });
      },

      // Hub to HubPage Connection
      addConnectionEdge(node) {
        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];
          const duplicateEdge = edges.find((edge) => edge.node?.pageId === node.pageId);
          if (duplicateEdge) {
            return prev;
          }
          return {
            connectionHubToHps: {
              edges: [...edges, { node }],
            },
          };
        });
      },
      updateConnectionEdge(pageId, node) {
        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];
          const updatedEdges = edges.map((edge) => {
            if (edge.node?.pageId !== pageId) {
              return edge;
            }
            return {
              ...edge,
              node: {
                ...edge.node,
                ...node,
              },
            };
          });

          return {
            connectionHubToHps: {
              edges: updatedEdges,
            },
          };
        });
      },
      deleteConnectionEdge(pageId) {
        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];
          const updatedEdges = edges.filter((edge) => edge.node?.pageId !== pageId);

          return {
            connectionHubToHps: {
              edges: updatedEdges,
            },
          };
        });
      },
      updateConnectionEdgePageOrder(pageId, direction) {
        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];

          // get the current page order
          const idx = edges.findIndex((edge) => edge.node?.pageId === pageId);
          if (idx === -1) {
            return prev;
          }
          const order = edges[idx].node?.pageOrder ?? 0;

          // track the other order when swapping
          const otherIdx = edges.findIndex(
            (edge) => edge.node?.pageOrder === order + (direction === 'increase' ? 1 : -1)
          );
          const otherOrder = edges[otherIdx]?.node?.pageOrder ?? 0;

          return {
            connectionHubToHps: {
              edges: edges.map((edge, i) => {
                return {
                  ...edge,
                  node: {
                    ...edge.node,
                    pageOrder: i === idx ? otherOrder : i === otherIdx ? order : edge.node?.pageOrder,
                  },
                };
              }),
            },
          };
        });
      },

      // Metadata Share Icons
      addMetadataShareIcon(node) {
        set((prev) => {
          return {
            metadata: {
              design: prev?.metadata?.design,
              navMeta: prev?.metadata?.navMeta,
              shareIcons: [...(prev?.metadata?.shareIcons ?? []), node],
            },
          };
        });
      },
      updateMetadataShareIcon(idx, node) {
        set((prev) => {
          const shareIcons = prev?.metadata?.shareIcons ?? [];
          const updatedShareIcons = shareIcons.map((icon, i) => {
            if (i !== idx) {
              return icon;
            }

            return {
              icon: node?.icon ?? icon?.icon,
              title: node?.title ?? icon?.title,
              url: node?.url ?? icon?.url,
            };
          });

          return {
            metadata: {
              design: prev?.metadata?.design,
              navMeta: prev?.metadata?.navMeta,
              shareIcons: updatedShareIcons,
            },
          };
        });
      },
      deleteMetadataShareIcon(idx) {
        set((prev) => {
          const shareIcons = prev?.metadata?.shareIcons ?? [];
          const updatedShareIcons = shareIcons.filter((_, i) => i !== idx);

          return {
            metadata: {
              design: prev?.metadata?.design,
              navMeta: prev?.metadata?.navMeta,
              shareIcons: updatedShareIcons,
            },
          };
        });
      },
      updateConnectionCatalogItem(pageId, node, action) {
        if (!pageId) {
          return;
        }

        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];

          const pageConnection = edges.find((edge) => edge.node?.pageId === pageId);
          if (!pageConnection) {
            return prev;
          }

          const catalogNodes = [...(pageConnection?.node?.hubPage?.node?.connectionHpToCs?.edges ?? [])];

          const catalogNodeIndex = catalogNodes?.findIndex((n) => n?.node?.catalogId === node?.node?.catalogId);
          if (action === 'add' && catalogNodeIndex > -1) {
            return prev;
          }
          if (action === 'delete' && (catalogNodeIndex === -1 || catalogNodes?.length === 0)) {
            return prev;
          }

          if (action === 'add') {
            catalogNodes.push({
              node: {
                ...node?.node,
                catalogOrder: catalogNodes?.length + 1,
              },
            });
          } else if (action === 'delete') {
            catalogNodes.splice(catalogNodeIndex, 1);
          }

          // sort the catalog nodes by catalogOrder
          const updatedCatalogNodes = catalogNodes
            .sort((a, b) => (a?.node?.catalogOrder ?? 0) - (b?.node?.catalogOrder ?? 0))
            .map((edge, idx) => {
              return {
                ...edge,
                node: {
                  ...edge.node,
                  catalogOrder: idx + 1,
                },
              };
            });

          return {
            connectionHubToHps: {
              edges: edges.map((edge) => {
                if (edge.node?.pageId !== pageId) {
                  return edge;
                }

                return {
                  ...edge,
                  node: {
                    ...edge?.node,
                    hubPage: edge?.node?.hubPage
                      ? {
                          ...edge?.node?.hubPage,
                          node: {
                            ...edge?.node?.hubPage?.node,
                            connectionHpToCs: edge?.node?.hubPage?.node?.connectionHpToCs
                              ? {
                                  ...edge?.node?.hubPage?.node?.connectionHpToCs,
                                  edges: updatedCatalogNodes,
                                }
                              : null,
                          },
                        }
                      : null,
                  },
                };
              }),
            },
          };
        });
      },
      reorderConnectionCatalogItems(pageId, sourceIndexOrNodes, destinationIndex) {
        if (!pageId) {
          return;
        }

        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];

          const pageConnection = edges.find((edge) => edge.node?.pageId === pageId);
          if (!pageConnection) {
            return prev;
          }

          const catalogNodes = [...(pageConnection?.node?.hubPage?.node?.connectionHpToCs?.edges ?? [])];

          let updatedCatalogNodes: DeepWritable<PageCatalogItem[]>;

          // Handle case when first argument is an array of nodes
          if (Array.isArray(sourceIndexOrNodes)) {
            updatedCatalogNodes = sourceIndexOrNodes.map((catalog, idx) => {
              return {
                node: {
                  catalog: {
                    node: catalog as NonNullable<DeepWritable<PageCatalogItem['node']['catalog']>>['node'],
                  },
                  catalogId: String(catalog?.id),
                  catalogOrder: idx,
                },
              };
            });
          } else if (typeof sourceIndexOrNodes === 'number' && typeof destinationIndex === 'number') {
            const sourceIndex = sourceIndexOrNodes;

            if (catalogNodes.length <= 1 || sourceIndex === destinationIndex) {
              return prev;
            }

            const nodesCopy = [...catalogNodes];
            const [movedItem] = nodesCopy.splice(sourceIndex, 1);
            nodesCopy.splice(destinationIndex, 0, movedItem);

            updatedCatalogNodes = nodesCopy.map((edge, idx) => {
              return {
                ...edge,
                node: {
                  ...edge.node,
                  catalogOrder: idx + 1,
                },
              };
            });
          } else {
            return prev;
          }

          return {
            connectionHubToHps: {
              edges: edges.map((edge) => {
                if (edge.node?.pageId !== pageId) {
                  return edge;
                }

                return {
                  ...edge,
                  node: {
                    ...edge?.node,
                    hubPage: edge?.node?.hubPage
                      ? {
                          ...edge?.node?.hubPage,
                          node: {
                            ...edge?.node?.hubPage?.node,
                            connectionHpToCs: edge?.node?.hubPage?.node?.connectionHpToCs
                              ? {
                                  ...edge?.node?.hubPage?.node?.connectionHpToCs,
                                  edges: updatedCatalogNodes,
                                }
                              : null,
                          },
                        }
                      : null,
                  },
                };
              }),
            },
          };
        });
      },
      updateConnectionPageForm(pageId, formNode) {
        if (!pageId) {
          return;
        }

        set((prev) => {
          const edges = prev?.connectionHubToHps?.edges ?? [];

          return {
            connectionHubToHps: {
              edges: edges.map((edge) => {
                if (edge.node?.pageId !== pageId) {
                  return edge;
                }

                return {
                  ...edge,
                  node: {
                    ...edge?.node,
                    hubPage: edge?.node?.hubPage
                      ? {
                          ...edge?.node?.hubPage,
                          node: {
                            ...edge?.node?.hubPage?.node,
                            form: {
                              node: {
                                ...(formNode as PageFormNode),
                              },
                            },
                          },
                        }
                      : null,
                  },
                };
              }),
            },
          };
        });
      },
      //
      // End
    }),
    {
      equality: isDeepEqual,
      limit: 100,
      partialize(state) {
        const config: Partial<HubStore> = {};
        // partialize the state to avoid __config in temporal state
        const { __config, ...rest } = state;

        for (const key in rest) {
          // @ts-ignore
          if (typeof rest[key as keyof HubStore] === 'function') {
            continue;
          }

          // @ts-ignore
          config[key as keyof HubStore] = rest[key as keyof HubStore];
        }

        return config as HubStore;
      },
      handleSet(handleSet) {
        return throttle(handleSet, 1000);
      },
    }
  )
);

const nullifyStoreData = (prev: HubStore, initData: typeof initHubStore): Partial<HubStore> => {
  const newState = {} as DeepNullable<Partial<HubStore>>;

  for (const key in prev) {
    const keyValue = prev?.[key as keyof HubStore];

    if (key === '__config') {
      continue;
    }
    if (typeof keyValue === 'function') {
      continue;
    }

    newState[key as keyof HubStore] = null;
  }

  return {
    ...(newState as Partial<HubStore>),
    ...initData,
  };
};

export const useEditHubStoreTemporal = <T>(selector: (state: TemporalState<HubStore>) => T) =>
  useStoreWithEqualityFn(useEditHubStore.temporal, selector, (pastState, currentState) =>
    isDeepEqual(pastState, currentState)
  );

/**
 * Whether the Edit Hub UI should be disabled
 */
export const useIsUiDisabled = () => {
  const fallback = useEditHubStore((state) => state.__config.isFallback);
  const status = useEditHubStore((state) => state?.status);
  const id = useEditHubStore((state) => state?.id);

  return useMemo(() => {
    if (fallback) {
      return true;
    }

    if (!id) {
      return true;
    }

    if (getIsStatus(status, PostStatusEnum.Trash)) {
      return true;
    }

    return false;
  }, [fallback, status, id]);
};

/**
 * Whether the Edit Hub can be saved in the current state
 */
export const useCanSaveHub = () => {
  const id = useEditHubStore((state) => state?.id);
  const status = useEditHubStore((state) => state?.status);
  const template = useEditHubStore((state) => state?.template);
  const title = useEditHubStore((state) => state?.title);
  const description = useEditHubStore((state) => state?.description);
  const group = useEditHubStore((state) => state?.group);
  const slug = useEditHubStore((state) => state?.slug);

  const result = useMemo(() => {
    if (!id) {
      return {
        canSave: false,
        errors: [
          {
            message: 'Please select a template',
            field: 'template',
          },
        ],
      };
    }
    if (getIsStatus(status, PostStatusEnum.Trash)) {
      return {
        canSave: false,
        errors: [
          {
            message: 'Cannot save a hub that is in the trash. Please restore the hub first.',
            field: 'status',
          },
        ],
      };
    }

    const errors = [];
    if (!template) {
      errors.push({
        message: 'Please select a template',
        field: 'template',
      });
    }

    if (!title) {
      errors.push({
        message: 'Please enter a title',
        field: 'title',
      });
    }

    if (!description) {
      errors.push({
        message: 'Please enter a description so you can identify this hub in the future',
        field: 'description',
      });
    }

    if (!group) {
      errors.push({
        message: 'Please enter a group. This is important for analytics',
        field: 'group',
      });
    }

    if (!slug) {
      errors.push({
        message: 'Please enter a slug',
        field: 'slug',
      });
    }

    if (errors.length) {
      return {
        canSave: false,
        errors,
      };
    }

    return {
      canSave: true,
      errors,
    };
  }, [id, status, template, title, description, group, slug]);

  return result;
};
